import React, { useEffect, useMemo, useState } from "react";
import reduce from "lodash/reduce";
import filter from "lodash/filter";
import includes from "lodash/includes";

import { useForm } from "react-hook-form";
import styled from "styled-components";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import ReplayIcon from "@mui/icons-material/Replay";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useSnackbar } from "notistack";

import Button from "../shared/Button";
import { ESTATE_DISPLAY_STATUS, PURPOSE_STATUS } from "../../helpers/constants";
import useRepresentatives from "../hooks/useRepresentative";
import useOffices from "../hooks/useOffices";
import useEstatesFilterStore from "../stores/useEstatesFilterStore";
import AutocompleteController from "../forms/AutocompleteController";
import CheckboxItemGroupController from "../forms/CheckboxItemGroupController";
import { get, map, size, sortBy, toLower } from "lodash";
import CheckboxController from "../forms/CheckboxController";
import { Typography } from "@mui/material";
import useFilterPreference from "../hooks/useFilterPreference";
import { getDefaultOffices } from "../../helpers/utils";
import PreferenceService from "../../services/PreferenceService";

const QuickFilterSummary = ({
  control,
  currentPurposeStatus,
  estateDisplaysStatusById,
  representativesData,
  representativesById,
  officesData,
  officesById,
  purposeStatusIsEmpty,
  displayStatusIsEmpty,
  representativesIsEmpty,
  officesIsEmpty,
}) => {
  const filterIsEmpty =
    purposeStatusIsEmpty &&
    displayStatusIsEmpty &&
    representativesIsEmpty &&
    officesIsEmpty;
  return (
    <Box
      className="filter-container summary d-flex flex-column"
      sx={{ m: 3, mt: 0, mb: 0 }}
    >
      <p className="filter-label">Filtre rapide</p>
      <div className="d-flex flex-wrap">
        {!purposeStatusIsEmpty &&
          PURPOSE_STATUS.filter(({ id }) =>
            includes(currentPurposeStatus, id)
          ).map((item) => (
            <FormControlLabel
              key={`purpose-status-q-filter-${item.id}`}
              control={
                <CheckboxItemGroupController
                  name="purposeStatus"
                  control={control}
                  item={item}
                  icon={<RadioButtonUncheckedIcon fontSize="ihnerit" />}
                  checkedIcon={<CheckCircleIcon fontSize="ihnerit" />}
                  sx={{
                    "&.Mui-checked": {
                      color: "var(--green-color)",
                    },
                  }}
                  disabled
                />
              }
              label={item.name}
              slotProps={{
                typography: {
                  sx: {
                    fontSize: 12,
                    color: "var(--light-grey-text)",
                    fontWeight: 700,
                    textTransform: "capitalize",
                  },
                },
              }}
            />
          ))}
        {!displayStatusIsEmpty && (
          <AutocompleteController
            name="displayStatus"
            control={control}
            options={ESTATE_DISPLAY_STATUS}
            optionsById={estateDisplaysStatusById}
            className="filter-input"
            size="small"
            disabled
            sx={{ mr: 1 }}
          />
        )}
        {!representativesIsEmpty && (
          <AutocompleteController
            name="representatives"
            control={control}
            options={representativesData}
            optionsById={representativesById}
            getOptionLabel={(option, optionsById) => {
              const representative = optionsById[option];
              return `${representative.firstName} ${representative.name}`;
            }}
            className="filter-input"
            size="small"
            disabled
            sx={{ mr: 1 }}
          />
        )}
        {!officesIsEmpty && (
          <AutocompleteController
            name="offices"
            control={control}
            options={officesData}
            optionsById={officesById}
            className="filter-input"
            size="small"
            disabled
            sx={{ mr: 1 }}
          />
        )}
      </div>
      {filterIsEmpty && <Typography>Aucun filtre séléctionné</Typography>}
    </Box>
  );
};

const QuickFilter = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [openQuickFilter, setOpenQuickFilter] = useState(false);
  const toggleFilter = (value) => () => setOpenQuickFilter(value);

  const purpose = useEstatesFilterStore((state) => state.purpose);
  const purposeStatus = useEstatesFilterStore((state) => state.purposeStatus);
  const representatives = useEstatesFilterStore(
    (state) => state.representatives
  );
  const offices = useEstatesFilterStore((state) => state.offices);
  const displayStatus = useEstatesFilterStore((state) => state.displayStatus);
  const resetQuickFilter = useEstatesFilterStore(
    (state) => state.resetQuickFilter
  );
  const isTopParent = useEstatesFilterStore((state) => state.isTopParent);
  const submitFilter = useEstatesFilterStore((state) => state.submitFilter);

  const { control, getValues, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      purposeStatus,
      representatives,
      offices,
      displayStatus,
      isTopParent,
    },
  });

  useEffect(() => {
    setValue("purposeStatus", purposeStatus);
  }, [purposeStatus]);

  useEffect(() => {
    setValue("representatives", representatives);
  }, [representatives]);

  useEffect(() => {
    setValue("offices", offices);
  }, [offices]);

  useEffect(() => {
    setValue("displayStatus", displayStatus);
  }, [displayStatus]);

  useEffect(() => {
    setValue("isTopParent", isTopParent);
  }, [isTopParent]);

  const { data: representativesData } = useRepresentatives();
  const { data: officesData } = useOffices();
  const { data: filterPreference, isFetching } = useFilterPreference();

  const { representativesOptions, representativesById } = useMemo(() => {
    const representativesOptions = sortBy(representativesData, (item) =>
      toLower(get(item, "firstName"))
    );
    const representativesById = reduce(
      representativesOptions,
      (acc, curr) => {
        if (!acc[curr.id]) {
          acc[curr.id] = curr;
        }
        return acc;
      },
      {}
    );

    return {
      representativesOptions,
      representativesById,
    };
  }, [representativesData]);

  const { officesOptions, officesById } = useMemo(() => {
    const officesOptions = sortBy(officesData, (item) =>
      toLower(get(item, "name"))
    );
    const officesById = reduce(
      officesOptions,
      (acc, curr) => {
        if (!acc[curr.id]) {
          acc[curr.id] = curr;
        }
        return acc;
      },
      {}
    );
    return {
      officesOptions,
      officesById,
    };
  }, [officesData]);

  const estateDisplaysStatusById = useMemo(
    () =>
      reduce(
        ESTATE_DISPLAY_STATUS,
        (acc, curr) => {
          if (!acc[curr.id]) {
            acc[curr.id] = curr;
          }
          return acc;
        },
        {}
      ),
    []
  );

  const purposeStatusOption = useMemo(
    () => filter(PURPOSE_STATUS, (ps) => ps.purposeId === purpose),
    [purpose]
  );

  const onSubmit = (data) => {
    setOpenQuickFilter(false);
    submitFilter(data);
  };

  const currentPurposeStatus = useMemo(
    () => getValues("purposeStatus"),
    [watch("purposeStatus")]
  );
  const currentDisplayStatus = useMemo(
    () => getValues("displayStatus"),
    [watch("displayStatus")]
  );
  const currentRepresentatives = useMemo(
    () => getValues("representatives"),
    [watch("representatives")]
  );
  const currentOffices = useMemo(
    () => getValues("offices"),
    [watch("offices")]
  );

  useEffect(() => {
    if (!!filterPreference && !isFetching) {
      submitFilter({
        purpose: filterPreference.purposeId,
        purposeStatus: filterPreference.purposeStatusIds,
        displayStatus: filterPreference.displayStatusIds,
        representatives: filterPreference.representativeIds,
        offices: filterPreference.officeIds,
        isTopParent: filterPreference.isTopParent,
      });
    }
  }, [filterPreference, isFetching]);

  useEffect(() => {
    if (size(officesData) > 0 && !filterPreference && !isFetching) {
      const defaultOffices = getDefaultOffices(officesData);
      submitFilter({ offices: map(defaultOffices, (o) => o.id) });
    }
  }, [officesData, filterPreference, isFetching]);

  const saveFilter = async () => {
    try {
      const data = {
        id: !!filterPreference ? filterPreference.id : null,
        purposeId: purpose,
        purposeStatusIds: getValues("purposeStatus"),
        displayStatusIds: getValues("displayStatus"),
        representativeIds: getValues("representatives"),
        officeIds: getValues("offices"),
        isTopParent: getValues("isTopParent"),
      };
      const res = await PreferenceService.saveFilter(data);
      if (res) {
        enqueueSnackbar("Vos préférences de filtres ont été sauvegardées", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
        });
      } else {
        enqueueSnackbar("Une erreur est survenue", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const purposeStatusIsEmpty = currentPurposeStatus.length === 0;
  const displayStatusIsEmpty = currentDisplayStatus.length === 0;
  const representativesIsEmpty = currentRepresentatives.length === 0;
  const officesIsEmpty = currentOffices.length === 0;

  return (
    <StyleWrapper className="d-flex" onSubmit={handleSubmit(onSubmit)}>
      {!openQuickFilter ? (
        <QuickFilterSummary
          control={control}
          currentPurposeStatus={currentPurposeStatus}
          estateDisplaysStatusById={estateDisplaysStatusById}
          representativesData={representativesOptions}
          representativesById={representativesById}
          officesData={officesOptions}
          officesById={officesById}
          purposeStatusIsEmpty={purposeStatusIsEmpty}
          displayStatusIsEmpty={displayStatusIsEmpty}
          representativesIsEmpty={representativesIsEmpty}
          officesIsEmpty={officesIsEmpty}
        />
      ) : (
        <div className="filter-container">
          <FormControl
            sx={{ m: 3, mb: 0 }}
            component="fieldset"
            variant="standard"
          >
            <FormLabel className="filter-label" component="legend">
              Statut de transactions
            </FormLabel>
            <FormGroup className="d-flex flex-row">
              {purposeStatusOption
                .filter(
                  ({ id }) =>
                    openQuickFilter || includes(currentPurposeStatus, id)
                )
                .map((item) => (
                  <FormControlLabel
                    key={`purpose-status-q-filter-${item.id}`}
                    control={
                      <CheckboxItemGroupController
                        name="purposeStatus"
                        control={control}
                        item={item}
                        disabled={!openQuickFilter}
                      />
                    }
                    label={item.name}
                    slotProps={{
                      typography: {
                        sx: {
                          fontSize: 12,
                          color: "var(--light-grey-text)",
                          fontWeight: 700,
                          textTransform: "capitalize",
                        },
                      },
                    }}
                  />
                ))}
            </FormGroup>
          </FormControl>
          <div className="d-flex filter-group">
            <FormControl sx={{ m: 3 }} variant="standard">
              <FormLabel className="filter-label" component="legend">
                Statut de visibilité
              </FormLabel>
              <AutocompleteController
                name="displayStatus"
                control={control}
                options={ESTATE_DISPLAY_STATUS}
                optionsById={estateDisplaysStatusById}
                className="filter-input"
                size="small"
                disabled={!openQuickFilter}
              />
            </FormControl>
            <FormControl sx={{ m: 3 }} variant="standard">
              <FormLabel className="filter-label" component="legend">
                Représentants
              </FormLabel>
              <AutocompleteController
                name="representatives"
                control={control}
                options={representativesOptions}
                optionsById={representativesById}
                getOptionLabel={(option, optionsById) => {
                  const representative = optionsById[option];
                  return `${representative.firstName} ${representative.name}`;
                }}
                className="filter-input"
                size="small"
                disabled={!openQuickFilter}
              />
            </FormControl>
            <FormControl sx={{ m: 3 }} variant="standard">
              <FormLabel className="filter-label" component="legend">
                Bureaux
              </FormLabel>
              <AutocompleteController
                name="offices"
                control={control}
                options={officesOptions}
                optionsById={officesById}
                className="filter-input"
                size="small"
                disabled={!openQuickFilter}
              />
            </FormControl>
          </div>
          <div className="d-flex filter-group">
            <CheckboxController
              sx={{ ml: 3 }}
              name="isTopParent"
              control={control}
              label="Afficher uniquement les projets"
            />
          </div>
        </div>
      )}
      <div className="d-flex flex-column justify-content-between align-items-end shrink">
        <Button
          type="button"
          className={`display-btn ${openQuickFilter ? "close" : "open"}`}
          icon={<img src="/assets/icons/chevron-left.svg" />}
          onClick={toggleFilter(!openQuickFilter)}
        />
        <div className="d-flex action-btn-container">
          {openQuickFilter && (
            <>
              <Button
                className="save-filer-btn"
                type="button"
                variant="contained"
                color="warning"
                icon={<StarBorderIcon />}
                onClick={saveFilter}
              >
                Save filter
              </Button>
              <Button
                className="apply-btn"
                type="submit"
                variant="contained"
                color="primary"
              >
                Appliquer
              </Button>
            </>
          )}
          <Button
            type="button"
            className="reset-btn"
            color="danger"
            variant="outlined"
            icon={<ReplayIcon htmlColor="var(--light-grey-text)" />}
            onClick={resetQuickFilter}
            noBorder
          />
        </div>
      </div>
    </StyleWrapper>
  );
};

const StyleWrapper = styled.form`
  border-radius: 12px;
  background-color: var(--white);
  padding: 15px;
  margin: 0 15px;
  margin-top: 15px;
  box-shadow: 0px 0px 10px -6px rgba(130, 130, 130, 1);

  .MuiFormControl-root {
    margin-top: 0;
  }

  .filter-container {
    flex-grow: 1;
    &.summary .filter-label {
      margin-bottom: 0;
    }
  }

  .filter-label {
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    font-family: "Manrope";
    color: var(--text-color);
  }

  .shrink {
    flex-shrink: 0;
  }

  .filter-input {
    min-width: 200px;
  }

  .action-btn-container {
    gap: 10px;
  }

  .display-btn {
    width: 30px;
    height: 30px;
    background-color: var(--menu-active-color);
    &.close img {
      transform: rotate(90deg);
    }

    &.open img {
      transform: rotate(270deg);
    }
  }

  .filter-group > .MuiFormControl-root {
    padding: 10px 24px;
    margin: 0;

    &:not(:last-child) {
      border-right: 1px solid var(--light-border);
    }
  }

  .save-filer-btn {
    padding-left: 15px;
    padding-right: 15px;
  }
`;

export default QuickFilter;
